// OptionsPanel - Interrupteurs pour les paramètres de cotation
// BSI : géré automatiquement par l'intelligence — sélectionnez BSA/BSB/BSC dans l'onglet Soins BSI

import { OPTIONS, OptionToggle } from '../data/careData';

interface OptionsPanelProps {
  options: Record<string, boolean>;
  onToggleOption: (optionId: string) => void;
  bsiType: string | null;
  onBSITypeChange: (bsiType: string | null) => void;
}

export default function OptionsPanel({
  options,
  onToggleOption,
}: OptionsPanelProps) {
  const deployOptions = OPTIONS.filter((o) => o.category === 'deplacement');
  const majorationOptions = OPTIONS.filter((o) => o.category === 'majoration');
  const contexteOptions = OPTIONS.filter((o) => o.category === 'contexte');
  const patientOptions = OPTIONS.filter((o) => o.category === 'patient');

  const activeCount = Object.values(options).filter(Boolean).length;

  return (
    <div className="bg-background-50 border border-background-200/60 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground-800 flex items-center gap-2">
          <i className="ri-settings-3-line text-secondary-500 text-base"></i>
          Majorations et contexte
        </h3>
        {activeCount > 0 && (
          <span className="text-xs font-medium text-secondary-600 bg-secondary-50 px-2 py-0.5 rounded-full">
            {activeCount} activée{activeCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2.5">
        {/* Majorations */}
        {majorationOptions.map((opt) => (
          <ToggleRow key={opt.id} option={opt} active={!!options[opt.id]} onToggle={() => onToggleOption(opt.id)} />
        ))}

        {/* Séparateur */}
        <div className="col-span-full my-1 border-t border-background-100"></div>

        {/* Contexte */}
        {contexteOptions.map((opt) => (
          <ToggleRow key={opt.id} option={opt} active={!!options[opt.id]} onToggle={() => onToggleOption(opt.id)} />
        ))}

        {/* Patient */}
        {patientOptions.length > 0 && (
          <>
            <div className="col-span-full my-1 border-t border-background-100"></div>
            {patientOptions.map((opt) => (
              <ToggleRow key={opt.id} option={opt} active={!!options[opt.id]} onToggle={() => onToggleOption(opt.id)} />
            ))}
          </>
        )}
      </div>

      <p className="text-[10px] text-foreground-300 mt-4">
        Pour activer le BSI, sélectionnez un forfait BSA, BSB ou BSC dans l&apos;onglet <strong>Soins BSI</strong>. L&apos;intelligence applique automatiquement toutes les règles.
      </p>
    </div>
  );
}

function ToggleRow({ option, active, onToggle }: { option: OptionToggle; active: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-secondary-100 border border-secondary-300/50'
          : 'bg-background-50 border border-background-200/40 hover:bg-background-100'
      }`}
    >
      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        active ? 'bg-secondary-500 border-secondary-500' : 'border-background-300'
      }`}>
        {active && <i className="ri-check-line text-white text-[10px] font-bold"></i>}
      </div>
      <i className={`${option.icon} text-xs ${active ? 'text-secondary-600' : 'text-foreground-400'}`}></i>
      <span className={`text-xs font-medium ${active ? 'text-foreground-800' : 'text-foreground-500'}`}>
        {option.label}
      </span>
    </button>
  );
}